import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import styles from "./WorkspacesPage.module.css";
import default_theme from "assets/themes/themes";

import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fastApiURL } from "../../../utils/config";
import SignUpPromptOverlay from "components/Auth/SignUpPromptOverlay";
import {
  fetchProjectsRequest,
  createProjectRequest,
  updateProjectRequest,
  fetchFrameworksRequest,
  deleteProjectRequest,
} from "../../../redux/projects/projectsSlice";
import ProjectCard from "components/projectCards/ProjectCard";
import WorkspacesPageDialog from "components/dialogs/WorkSpacesPage/workSpacesPageDialog";
import DeleteProjectDialog from "components/dialogs/WorkSpacesPage/DeleteProjectDialog";
import WorkspacesIntro from "components/WorkspacesIntro/WorkspacesIntro";
import { blueTextFieldStyles } from "assets/themes/textFieldStyles";
import TokenManager from "../../../utils/tokenManager";

// Keep all form defaults in one place so create and edit flows reset identically.
const createEmptyForm = () => ({
  name: "",
  description: "",
  imagePreview: null,
  imageFile: null,
  imageRemoteUrl: null,
  frameworkId: "",
});

const WorkspacesPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const authState = useSelector((state) => state.auth);
  const isAuthenticated = authState?.isAuthenticated ?? false;
  const { items: projectItems, loading } = useSelector(
    (state) => state.projects
  );
  const [form, setForm] = useState(createEmptyForm());
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSignUpOverlay, setShowSignUpOverlay] = useState(false);
  const [editingProjectId, setEditingProjectId] = useState(null);
  const { frameworks } = useSelector((state) => state.projects);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);

  // The dialog needs the framework list before a user can create or edit a project.
  useEffect(() => {
    dispatch(fetchFrameworksRequest());
  }, [dispatch]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const token = TokenManager.getAccessToken();
    if (!token) {
      console.error("User not authenticated");
      return;
    }

    // Show an immediate local preview while the upload happens in the background.
    const preview = URL.createObjectURL(file);
    setForm((prev) => ({
      ...prev,
      imageFile: file,
      imagePreview: preview,
    }));

    const formData = new FormData();
    formData.append("file", file);

    fetch(`${fastApiURL}/api/projects/upload-image`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    })
      .then(async (res) => {
        if (!res.ok) {
          throw new Error(`Failed to upload image (${res.status})`);
        }
        return res.json();
      })
      .then((data) => {
        setForm((prev) => ({
          ...prev,
          imageRemoteUrl: data.image_url || null,
        }));
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error(err);
      });
  };



  const handleOpenDialog = () => {
    if (!isAuthenticated) {
      setShowSignUpOverlay(true);
      return;
    }
    setForm(createEmptyForm());
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  const handleSaveProject = () => {
    if (!form.name.trim() || !form.description.trim()) return;

    // Use the same dialog for both update and create actions.
    if (editingProjectId) {
      dispatch(
        updateProjectRequest({
          projectId: editingProjectId,
          title: form.name.trim(),
          description: form.description.trim(),
          imageUrl: form.imageRemoteUrl || null,
          frameworkId: form.frameworkId,
        })
      );
    } else {
      dispatch(
        createProjectRequest({
          title: form.name.trim(),
          description: form.description.trim(),
          imageUrl: form.imageRemoteUrl || null,
          frameworkId: form.frameworkId,
        })
      );
    }

    setForm(createEmptyForm());
    setEditingProjectId(null);
    setIsDialogOpen(false);
  };

  // Project data is only fetched for authenticated users because guests cannot access it.
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchProjectsRequest());
    }
  }, [dispatch, isAuthenticated]);

  // Normalize the backend project shape into the card props used by this page.
  const projects = projectItems.map((p) => ({
    id: p.id,
    name: p.title,
    description: p.description,
    imagePreview: p.imageUrl || null,
    frameworkId: p.frameworkId,
    framework: p.framework,
  }));

  const filteredProjects = projects.filter((project) =>
    project.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleConfirmDelete = () => {
    if (!projectToDelete) return;

    dispatch(deleteProjectRequest({ projectId: projectToDelete.id }));

    setDeleteDialogOpen(false);
    setProjectToDelete(null);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setProjectToDelete(null);
  };

  return (
    <Box className={styles.pageContainer}>
      <Box className={styles.contentWrapper}>
        <WorkspacesIntro onStart={handleOpenDialog} />

        <Box className={styles.separator} />

        <Box className={styles.headerRow}>
          <Box>
            <Typography className={styles.sectionEyebrow}>
              Your projects
            </Typography>
          </Box>
          <Box className={styles.headerActions}>
            <TextField
              size="small"
              placeholder="Search projects"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                minWidth: 250,

                "& .MuiOutlinedInput-root": {
                  background: "rgba(255, 255, 255, 0.15)",
                  backdropFilter: "blur(12px)",
                  WebkitBackdropFilter: "blur(12px)",
                  borderRadius: "12px",
                  color: "#fff",

                  boxShadow: `
                    0 8px 32px rgba(31, 38, 135, 0.15),
                    inset 0 1px 1px rgba(255, 255, 255, 0.2)
                  `,

                  "& fieldset": {
                    border: "1px solid rgba(255, 255, 255, 0.25)",
                  },
                  "&:hover fieldset": {
                    border: "1px solid rgba(255, 255, 255, 0.25)",
                  },
                  "&.Mui-focused fieldset": {
                    border: "1px solid rgba(255, 255, 255, 0.25)",
                  },

                  "& input": {
                    color: "#fff",
                  },
                },

                "& .MuiInputBase-input::placeholder": {
                  color: "rgba(255,255,255,0.7)",
                  opacity: 1,
                },
              }}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenDialog}
              className={styles.createButton}
            >
              Create project
            </Button>
          </Box>
        </Box>

        <Divider className={styles.divider} />

        {isAuthenticated && (
          <Box className={styles.projectsList}>
            {filteredProjects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                onOpen={() => navigate(`/workspaces/projects/${project.id}`)}
                onEdit={() => {
                  setEditingProjectId(project.id);

                  // Rehydrate the dialog with the current project values before editing.
                  setForm({
                    name: project.name,
                    description: project.description,
                    imagePreview: project.imagePreview,
                    imageRemoteUrl: project.imagePreview?.replace(fastApiURL, ""),
                    imageFile: null,
                    frameworkId: project.frameworkId || "",
                  });

                  setIsDialogOpen(true);
                }}
                onDelete={() => {
                  setProjectToDelete(project);
                  setDeleteDialogOpen(true);
                }}
              />
            ))}
          </Box>
        )}

        <WorkspacesPageDialog
          open={isDialogOpen}
          onClose={handleCloseDialog}
          onSave={handleSaveProject}
          form={form}
          setForm={setForm}
          frameworks={frameworks}
          editingProjectId={editingProjectId}
          fileInputRef={fileInputRef}
          onImageChange={handleImageChange}
        />

        <DeleteProjectDialog
          open={deleteDialogOpen}
          onClose={handleCloseDeleteDialog}
          onConfirm={handleConfirmDelete}
          projectName={projectToDelete?.name}
        />

        <SignUpPromptOverlay
          open={showSignUpOverlay}
          onClose={() => setShowSignUpOverlay(false)}
        />
      </Box>
    </Box>
  );
};

export default WorkspacesPage;